import FIELDS from '../PoliceReportFields';
import moment from 'moment';
import PoliceReportConstants from '../police.constants';
import { isEmpty } from 'utils/TextUtils';

const {
  ITALIAN_ROLE_TYPE,
  ITA_GENDERS,
  ADULT_AGE,
  WS_MAX_STAY_DAYS = 30,
  ITA_MUNICIPALITIES, // ⬅️ añadido
  ITA_PROVINCES, // ⬅️ añadido (obj: { PD: "Padova", ... })
} = PoliceReportConstants;

// === Helpers ===
const valOf = v => (v && typeof v === 'object' && 'value' in v ? v.value : v);

function isAdult(date) {
  const birthDate = moment(date, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY']);
  const today = moment();
  return birthDate.isValid() && today.diff(birthDate, 'years') > ADULT_AGE;
}

// Comprueba si el valor de 'city' es válido como ISTAT (9 dígitos) o como label existente
function isValidMunicipalityValue(value) {
  const v = String(valOf(value) || '');
  if (!v) return false;
  if (/^\d{9}$/.test(v)) return true; // ISTAT 9
  // ¿Existe como label?
  const existsByLabel = ITA_MUNICIPALITIES?.some(m => m.label?.toLowerCase() === v.toLowerCase());
  return !!existsByLabel;
}

// Comprueba si se puede obtener una provincia válida: por 2 letras o por el Comune
function canResolveProvinceFrom(valueProvince, valueCity) {
  const code = String(valOf(valueProvince) || '');
  if (/^[A-Z]{2}$/.test(code) && ITA_PROVINCES?.[code]) return true;

  // Intentar desde el Comune:
  const cityVal = String(valOf(valueCity) || '');
  if (!cityVal) return false;

  let mun = null;
  if (/^\d{9}$/.test(cityVal)) {
    mun = ITA_MUNICIPALITIES?.find(m => m.value === cityVal);
  } else {
    mun = ITA_MUNICIPALITIES?.find(m => m.label?.toLowerCase() === cityVal.toLowerCase());
  }
  const provCode = mun?.meta?.province;
  return !!(provCode && ITA_PROVINCES?.[provCode]);
}

// En Alloggiati, esta función se usa contra el "guest" (no contrato).
function isMandatoryFieldEmpty(guest, field) {
  const val = f => valOf(guest?.[f]);
  const isEmptyField = f => isEmpty(val(f));

  const iso3Birth = val(FIELDS.BIRTHCOUNTRY_KEY); // ej. 'ITA'
  const isBirthItaly = iso3Birth === 'ITA';

  // Campos obligatorios base (huésped/schedina)
  const baseRequired = [
    FIELDS.ROLETYPE_KEY,
    FIELDS.SURNAME_KEY,
    FIELDS.NAME_KEY,
    FIELDS.GENDER_KEY,
    FIELDS.BIRTHDATE_KEY,
    FIELDS.BIRTHCOUNTRY_KEY,
    FIELDS.NATIONALITY_KEY,
    FIELDS.DOCUMENTISSUANCECOUNTRY_KEY,
  ];

  // Italia: Comune/Provincia obligatorios (nacimiento)
  // ⚠️ Provincia NO se considera vacía si se puede resolver por el Comune
  const italyBirthRequired = isBirthItaly ? [FIELDS.BIRTHCITY_KEY] : [];

  const requiredFields = [...baseRequired, ...italyBirthRequired];

  // Reglas normales de "vacío"
  if (requiredFields.includes(field)) {
    return isEmptyField(field);
  }

  // Reglas específicas para provincia de nacimiento:
  if (field === FIELDS.BIRTHPROVINCE_KEY && isBirthItaly) {
    // Si NO se puede resolver por provincia directa NI por Comune -> vacío
    const canResolve = canResolveProvinceFrom(
      guest?.[FIELDS.BIRTHPROVINCE_KEY],
      guest?.[FIELDS.BIRTHCITY_KEY]
    );
    return !canResolve;
  }

  return false;
}

function getFieldValidations(report, translate) {
  // `report` es el objeto completo (con .contract y .guests).
  const fieldValidations = {
    // ======== Guest-level ========

    [FIELDS.ROLETYPE_KEY]: {
      validate: guest => {
        const roleLabel = valOf(guest?.[FIELDS.ROLETYPE_KEY]);
        return !!roleLabel && ITALIAN_ROLE_TYPE?.includes(roleLabel);
      },
      error: translate?.('policeReport.error.role'),
    },

    [FIELDS.GENDER_KEY]: {
      validate: guest => {
        const g = String(valOf(guest?.[FIELDS.GENDER_KEY] || ''));
        return ITA_GENDERS?.includes(g); // ['M','F']
      },
      error: translate?.('policeReport.error.gender'),
    },

    [FIELDS.NAME_KEY]: {
      validate: guest => {
        const name = String(valOf(guest?.[FIELDS.NAME_KEY]) || '');
        return !!name && name.length <= 30;
      },
      error: translate?.('policeReport.error.name'),
    },

    [FIELDS.SURNAME_KEY]: {
      validate: guest => {
        const sn = String(valOf(guest?.[FIELDS.SURNAME_KEY]) || '');
        return !!sn && sn.length <= 50;
      },
      error: translate?.('policeReport.error.surname'),
    },

    [FIELDS.BIRTHDATE_KEY]: {
      validate: guest => {
        const raw = valOf(guest?.[FIELDS.BIRTHDATE_KEY]);
        const date = moment(raw, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
        return date.isValid() && date <= moment() && date.year() >= 1900;
      },
      error: translate?.('policeReport.error.birthDate'),
    },

    [FIELDS.BIRTHCOUNTRY_KEY]: {
      validate: guest => !!valOf(guest?.[FIELDS.BIRTHCOUNTRY_KEY]),
      error: translate?.('policeReport.error.birthCountry'),
    },

    [FIELDS.NATIONALITY_KEY]: {
      validate: guest => !!valOf(guest?.[FIELDS.NATIONALITY_KEY]),
      error: translate?.('policeReport.error.citizenship'),
    },

    // Italia (nacimiento): Comune válido (ISTAT 9 o label existente)
    [FIELDS.BIRTHCITY_KEY]: {
      validate: guest => {
        if (valOf(guest?.[FIELDS.BIRTHCOUNTRY_KEY]) !== 'ITA') return true;
        return isValidMunicipalityValue(guest?.[FIELDS.BIRTHCITY_KEY]);
      },
      error: translate?.('policeReport.error.birthMunicipality'),
    },

    // Italia (nacimiento): Provincia válida (2 letras) O bien resoluble desde el Comune
    [FIELDS.BIRTHPROVINCE_KEY]: {
      validate: guest => {
        if (valOf(guest?.[FIELDS.BIRTHCOUNTRY_KEY]) !== 'ITA') return true;
        return canResolveProvinceFrom(
          guest?.[FIELDS.BIRTHPROVINCE_KEY],
          guest?.[FIELDS.BIRTHCITY_KEY]
        );
      },
      error: translate?.('policeReport.error.birthProvince'),
    },

    // Documento (longitudes)
    [FIELDS.DOCUMENTTYPE_KEY]: {
      validate: guest => {
        const val = valOf(guest?.[FIELDS.DOCUMENTTYPE_KEY]);
        return !val || String(val).length <= 5;
      },
      error: translate?.('policeReport.error.docType'),
    },
    [FIELDS.DOCUMENTNUMBER_KEY]: {
      validate: guest => {
        const val = valOf(guest?.[FIELDS.DOCUMENTNUMBER_KEY]);
        return !val || String(val).length <= 20;
      },
      error: translate?.('policeReport.error.docNumber'),
    },

    // País de expedición
    [FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]: {
      validate: guest => !!valOf(guest?.[FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]),
      error: translate?.('policeReport.error.docIssuanceCountry'),
    },

    // Si expediente es ITA: Comune y Provincia
    [FIELDS.DOCUMENTISSUANCEMUNICIPALITY_KEY]: {
      validate: guest => {
        if (valOf(guest?.[FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]) !== 'ITA') return true;
        return isValidMunicipalityValue(guest?.[FIELDS.DOCUMENTISSUANCEMUNICIPALITY_KEY]);
      },
      error: translate?.('policeReport.error.docIssuanceMunicipality'),
    },
    [FIELDS.DOCUMENTISSUANCEPROVINCE_KEY]: {
      validate: guest => {
        if (valOf(guest?.[FIELDS.DOCUMENTISSUANCECOUNTRY_KEY]) !== 'ITA') return true;
        // Acepta 2 letras o que sea resoluble desde el Comune de expedición
        return canResolveProvinceFrom(
          guest?.[FIELDS.DOCUMENTISSUANCEPROVINCE_KEY],
          guest?.[FIELDS.DOCUMENTISSUANCEMUNICIPALITY_KEY]
        );
      },
      error: translate?.('policeReport.error.docIssuanceProvince'),
    },

    // ======== Contract-level ========
    [FIELDS.ENTRANCE_KEY]: {
      validate: () => {
        const ent = moment(report?.contract?.[FIELDS.ENTRANCE_KEY], [
          'DD-MM-YYYY',
          'YYYY-MM-DD',
          'DD/MM/YYYY',
        ]);
        return ent.isValid();
      },
      error: translate?.('policeReport.error.arrivalDate'),
    },
    [FIELDS.DEPARTURE_KEY]: {
      validate: () => {
        const ent = moment(report?.contract?.[FIELDS.ENTRANCE_KEY], [
          'DD-MM-YYYY',
          'YYYY-MM-DD',
          'DD/MM/YYYY',
        ]);
        const dep = moment(report?.contract?.[FIELDS.DEPARTURE_KEY], [
          'DD-MM-YYYY',
          'YYYY-MM-DD',
          'DD/MM/YYYY',
        ]);
        return (
          dep.isValid() &&
          ent.isValid() &&
          dep.diff(ent, 'days') > 0 &&
          dep.diff(ent, 'days') <= WS_MAX_STAY_DAYS
        );
      },
      error: translate?.('policeReport.validation.maxStayDays'),
    },
  };

  return fieldValidations;
}

export default { isMandatoryFieldEmpty, getFieldValidations };
